import type {
  ITemplateTag,
  IActivityTemplate,
  IBatchTemplate,
} from "@/api/Templates";
import type { ITagGroup } from "@medoma/models/templates";
import {
  type IMedicalCompetence,
  medicalCompetenceSchema,
  orderedCompetences,
} from "@medoma/models/shifts";
import { NO_GROUP_ID, NO_PATIENT_TYPE_SELECTED } from "./GroupForm/GroupSelect";

export type ITemplate = IActivityTemplate | IBatchTemplate;

export type IGroupedTemplates = {
  groupId: string;
  groupName: string;
  sortingNumber?: number;
  templates: ITemplate[];
};

export function groupTemplatesByTag(
  allTemplates: ITemplate[],
  allGroupTags: ITemplateTag[],
): { groups: IGroupedTemplates[]; ungroupedTemplates: ITemplate[] } {
  const groupMap = new Map<
    string,
    {
      groupId: string;
      groupName: string;
      sortingNumber?: number;
      templates: ITemplate[];
    }
  >();
  const ungroupedTemplates: ITemplate[] = [];

  for (const groupTag of allGroupTags) {
    groupMap.set(groupTag.id, {
      groupId: groupTag.id,
      groupName: parseGroupDisplayName(groupTag.displayName),
      sortingNumber: parseGroupSorting(groupTag.displayName),
      templates: [],
    });
  }

  for (const template of allTemplates) {
    const groupTags = filterTagsByGroup(template.tags, "group");

    if (groupTags.length === 0) {
      ungroupedTemplates.push(template);
    } else {
      const groupTag = groupTags[0];
      if (groupTag) {
        const existing = groupMap.get(groupTag.id);
        if (existing) {
          existing.templates.push(template);
        } else {
          ungroupedTemplates.push(template);
        }
      }
    }
  }

  const groups = [...groupMap.values()].sort((a, b) => {
    const sortingA = a.sortingNumber;
    const sortingB = b.sortingNumber;

    // Groups with sorting numbers come first
    if (sortingA !== undefined && sortingB !== undefined) {
      return sortingA - sortingB;
    }
    if (sortingA !== undefined) {
      return -1; // a comes before b
    }
    if (sortingB !== undefined) {
      return 1; // b comes before a
    }
    // Both have no sorting number, sort alphabetically by name
    return a.groupName.localeCompare(b.groupName);
  });

  return { groups, ungroupedTemplates };
}

/** Extract the group prefix from a tag displayName, e.g. "group" from "group:Foo". Returns empty string if no group prefix. */
export function parseTagGroup(displayName: string): string {
  const parts = displayName.split(":");
  return parts.length > 1 ? (parts[0] ?? "") : "";
}

/** Extract the name part after ":" from a tag displayName, e.g. "Foo" from "group:Foo". Returns the full displayName if no ":" is present. */
export function parseTagName(displayName: string): string {
  const parts = displayName.split(":");
  return parts.length > 1 ? parts.slice(1).join(":") : displayName;
}

/** Parse the display name of a competence tag (e.g. "competence:Cardiology" -> "Cardiology"). Wraps parseTagName for semantic clarity. */
export function parseCompetenceTagName(displayName: string): string {
  return parseTagName(displayName);
}

/** Extract the patient type from a group tag displayName, e.g. "HospitalAtHome" from "group:HospitalAtHome/Inskrivning". Returns empty string if no patient type prefix is found. */
export function parseGroupPatientType(displayName: string): string {
  const tagName = parseTagName(displayName);
  const slashIndex = tagName.indexOf("/");
  return slashIndex >= 0 ? tagName.substring(0, slashIndex) : "";
}

/** Extract the group display name from a group tag displayName, e.g. "Inskrivning" from "group:HospitalAtHome/Inskrivning" or "Group name" from "group:HospitalAtHome/1-Group name". Returns the full tag name if no "/" is present. */
export function parseGroupDisplayName(displayName: string): string {
  const tagName = parseTagName(displayName);
  const slashIndex = tagName.indexOf("/");
  if (slashIndex < 0) {
    return tagName;
  }
  const afterSlash = tagName.substring(slashIndex + 1);
  // Check if there's a sorting number prefix (format: "Number-GroupName")
  const hyphenIndex = afterSlash.indexOf("-");
  if (hyphenIndex >= 0) {
    // Check if the part before hyphen is a number
    const beforeHyphen = afterSlash.substring(0, hyphenIndex);
    if (/^\d+$/.test(beforeHyphen)) {
      // It's a sorting number, return the part after the hyphen
      return afterSlash.substring(hyphenIndex + 1);
    }
  }
  // No sorting number, return everything after the slash
  return afterSlash;
}

/** Extract the sorting number from a group tag displayName, e.g. 1 from "group:HospitalAtHome/1-Group name". Returns undefined if no sorting number is present. */
export function parseGroupSorting(displayName: string): number | undefined {
  const tagName = parseTagName(displayName);
  const slashIndex = tagName.indexOf("/");
  if (slashIndex < 0) {
    return undefined;
  }
  const afterSlash = tagName.substring(slashIndex + 1);
  const hyphenIndex = afterSlash.indexOf("-");
  if (hyphenIndex >= 0) {
    const beforeHyphen = afterSlash.substring(0, hyphenIndex);
    if (/^\d+$/.test(beforeHyphen)) {
      const sortingNumber = Number.parseInt(beforeHyphen, 10);
      return Number.isNaN(sortingNumber) ? undefined : sortingNumber;
    }
  }
  return undefined;
}

/** Filter tags by group */
export function filterTagsByGroup(
  tags: ReadonlyArray<ITemplateTag>,
  group: ITagGroup,
): ITemplateTag[] {
  return tags.filter((tag) => parseTagGroup(tag.displayName) === group);
}

/** Get tag IDs belonging to a specific group */
export function getTagIdsByGroup(
  tags: ReadonlyArray<ITemplateTag>,
  group: ITagGroup,
): string[] {
  return filterTagsByGroup(tags, group).map((tag) => tag.id);
}

/** Get the default groupId from a template's tags (first match, or "no-group") */
export function getDefaultGroupId(tags: ReadonlyArray<ITemplateTag>): string {
  const groupTags = filterTagsByGroup(tags, "group");
  return groupTags[0]?.id ?? NO_GROUP_ID;
}

/**
 * Collect tag IDs from the groupId field.
 * If the groupId is NO_GROUP_ID, return an empty array (no tags should be stored).
 * If the groupId is NO_PATIENT_TYPE_SELECTED (placeholder value), treat it as NO_GROUP_ID.
 */
export function getGroupIdFromForm({ groupId }: { groupId: string }): string[] {
  if (groupId === NO_GROUP_ID || groupId === NO_PATIENT_TYPE_SELECTED) {
    return [];
  }

  return [groupId];
}

/** Get default competence tag IDs from a template's tags */
export function getDefaultCompetenceTagIds(
  tags: ReadonlyArray<ITemplateTag>,
): string[] {
  return getTagIdsByGroup(tags, "competence");
}

/**
 * Collect all tag IDs from the form (group + competence tags).
 * Combines the groupId and competenceTagsIds into a single array.
 */
export function getTagIdsFromForm({
  groupId,
  competenceTagsIds,
}: {
  groupId: string;
  competenceTagsIds: string[];
}): string[] {
  return [...getGroupIdFromForm({ groupId }), ...competenceTagsIds];
}

/**
 * Compute the diff between enabled competences and existing competence tags.
 * Returns displayNames to create and tag IDs to delete.
 */
export function computeCompetenceTagSync(
  enabledCompetences: ReadonlyArray<IMedicalCompetence>,
  existingCompetenceTags: ReadonlyArray<ITemplateTag>,
): {
  toCreate: string[];
  toDelete: string[];
} {
  const existingNames = new Set(
    existingCompetenceTags.map((tag) =>
      parseCompetenceTagName(tag.displayName),
    ),
  );

  const toCreate = enabledCompetences
    .filter((competence) => !existingNames.has(competence))
    .map((competence) => `competence:${competence}`);

  const enabledSet = new Set<string>(enabledCompetences);
  const toDelete = existingCompetenceTags
    .filter((tag) => !enabledSet.has(parseCompetenceTagName(tag.displayName)))
    .map((tag) => tag.id);

  return { toCreate, toDelete };
}

/**
 * Sort competence tags by the canonical order of medical competences.
 * Tags whose displayName parses to a known competence appear in that order;
 * others are placed at the end.
 */
export function sortCompetenceTagsByOrderedCompetences(
  competenceTags: ReadonlyArray<ITemplateTag>,
): ITemplateTag[] {
  return [...competenceTags].sort((a, b) => {
    const resultA = medicalCompetenceSchema.safeParse(
      parseCompetenceTagName(a.displayName),
    );
    const resultB = medicalCompetenceSchema.safeParse(
      parseCompetenceTagName(b.displayName),
    );
    const indexA = resultA.success
      ? orderedCompetences.indexOf(resultA.data)
      : 999;
    const indexB = resultB.success
      ? orderedCompetences.indexOf(resultB.data)
      : 999;
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });
}
